'use client';

import { useTranslation } from '@/i18n/LocaleProvider';
import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PageSection } from '@/components';
import { useStaggerIn } from '@/hooks/useStaggerIn';
import { MONO_FONT } from '@/theme';

const ENGINES = [
  {
    favicon: '/assets/engines/openai-for-light.webp',
    name: 'ChatGPT',
    co: 'OpenAI',
    tier: 'principal',
  },
  {
    favicon: '/assets/engines/perplexity.webp',
    name: 'Perplexity',
    co: 'Perplexity AI',
    tier: 'principal',
  },
  {
    favicon: '/assets/engines/gemini.webp',
    name: 'Gemini',
    co: 'Google DeepMind',
    tier: 'principal',
  },
  {
    favicon: '/assets/engines/claude.webp',
    name: 'Claude',
    co: 'Anthropic',
    tier: 'principal',
  },
  {
    favicon: '/assets/engines/google.webp',
    name: 'Google AIO',
    co: 'Google Search',
    tier: 'principal',
  },
  {
    favicon: '/assets/engines/grok-ai.webp',
    name: 'Grok',
    co: 'xAI',
    tier: 'adicional',
  },
  {
    favicon: '/assets/engines/meta-ai.webp',
    name: 'Meta Muse',
    co: 'Meta',
    tier: 'beta',
  },
  {
    favicon: '/assets/engines/copilot.webp',
    name: 'Copilot',
    co: 'Microsoft',
    tier: 'adicional',
  },
  {
    favicon: '/assets/engines/deepseek.webp',
    name: 'DeepSeek',
    co: 'DeepSeek AI',
    tier: 'adicional',
  },
  {
    favicon: '/assets/engines/mistral.webp',
    name: 'Mistral',
    co: 'Mistral AI',
    tier: 'beta',
  },
];

const TIER_CONFIG = {
  principal: { label: 'LLM principal', color: 'text.accent' },
  adicional: { label: 'LLM adicional', color: 'text.muted' },
  beta: { label: 'Beta', color: 'text.muted' },
};

export default function Engines() {
  const t = useTranslation();
  const gridRef = useStaggerIn({ stagger: 0.06 });

  return (
    <PageSection
      eyebrow="06 / Cobertura"
      title={
        <>
          <span className="accent">
            <T>Donde te buscan</T>
          </span>
          <T> y donde tienes que estar.</T>
        </>
      }
      lede="Las respuestas reemplazaron los enlaces azules. Hoy son párrafos generativos —y cada uno cita un puñado de fuentes. Rastreamos hasta diez motores con el mismo prompt para que los datos sean comparables."
    >
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(5, 1fr)',
          },
          borderTop: '1px solid #e2e5e3',
          borderLeft: '1px solid #e2e5e3',
        }}
      >
        {ENGINES.map((engine) => (
          <Box
            key={engine.name}
            sx={{
              p: { xs: '20px 16px', md: '28px 20px' },
              borderRight: '1px solid #e2e5e3',
              borderBottom: '1px solid #e2e5e3',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              transition: 'background 0.15s',
              '&:hover': { bgcolor: 'grey.50' },
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                border: '1px solid #e2e5e3',
                display: 'grid',
                placeItems: 'center',
                mb: 1,
                overflow: 'hidden',
                bgcolor: 'common.white',
              }}
            >
              <img
                src={engine.favicon}
                alt={t('Logo de {engine}', { engine: engine.name })}
                width={24}
                height={24}
                style={{ objectFit: 'contain' }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: '17px',
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '-0.005em',
              }}
            >
              <T>{engine.name}</T>
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                color: 'text.muted',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              <T>{engine.co}</T>
            </Typography>
            <Box
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '10.5px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                pt: '8px',
                alignSelf: 'flex-start',
                mt: 0.5,
                color: TIER_CONFIG[engine.tier].color,
              }}
            >
              <T>{TIER_CONFIG[engine.tier].label}</T>
            </Box>
          </Box>
        ))}
      </Box>
    </PageSection>
  );
}
